/**
 * StateEmergenciesPage · v2.7.30 (2026-02-21)
 * ===========================================
 * Live feed of Oklahoma state-level public safety alerts surfaced by
 * the NWS CAP gateway: AMBER, Silver, Blue, Kasey + civil emergency
 * messages. Auto-refreshes every 60s. Mounts useWeatherStream so the
 * EAS attention tone still fires on push-worthy items.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldAlert, RefreshCw } from "lucide-react";
import adminApi from "@/lib/adminApi";
import useWeatherStream from "@/hooks/useWeatherStream";

const CATEGORY_STYLE = {
  AMBER:  { cls: "bg-amber-500/15 text-amber-300 border-amber-400/50",   label: "AMBER ALERT" },
  SILVER: { cls: "bg-slate-500/15 text-slate-300 border-slate-400/50",   label: "SILVER ALERT" },
  BLUE:   { cls: "bg-blue-500/15 text-blue-300 border-blue-400/50",      label: "BLUE ALERT" },
  KASEY:  { cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/50", label: "KASEY ALERT" },
};

const fmtExpiry = (iso) => {
  if (!iso) return "—";
  try {
    const mins = Math.round((new Date(iso) - new Date()) / 60000);
    if (mins < 0) return "expired";
    if (mins < 60) return `${mins} min left`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m left`;
  } catch { return iso; }
};

export default function StateEmergenciesPage({ embedded = false }) {
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);
  useWeatherStream({ enabled: true, audibleAlarm: true });

  const reload = useCallback(() => {
    setBusy(true);
    adminApi.get("/admin/weather/alerts/state")
      .then((r) => setAlerts(r.data || []))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 10000); return () => clearInterval(id); }, [reload]);
  useEffect(() => {
    const onAlert = () => reload();
    window.addEventListener("rtg:weather_alert", onAlert);
    return () => window.removeEventListener("rtg:weather_alert", onAlert);
  }, [reload]);

  return (
    <div className={embedded ? "p-4 sm:p-6" : "min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8"} data-testid="state-emergencies-page">
      <div className={embedded ? "max-w-full space-y-6" : "max-w-5xl mx-auto space-y-6"}>
        {!embedded && (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider">
                <ArrowLeft className="h-4 w-4"/> Admin Console
              </Link>
              <div className="h-6 w-px bg-zinc-800"/>
              <ShieldAlert className="h-6 w-6 text-fuchsia-300"/>
              <h1 className="text-2xl font-extrabold tracking-tight">State Emergencies</h1>
            </div>
            <Button onClick={reload} disabled={busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="state-emergencies-refresh">
              <RefreshCw className="h-4 w-4 mr-2"/>Refresh
            </Button>
          </div>
        )}

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-fuchsia-400/40 flex items-center justify-between">
            <span>Active Oklahoma Public Safety Alerts</span>
            <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40">{alerts.length}</Badge>
          </div>
          {alerts.length === 0 && (
            <div className="py-12 text-center text-sm text-zinc-500">
              No active state-level alerts at this time. Feed refreshes every 60 seconds.
            </div>
          )}
          <div className="space-y-3">
            {alerts.map((a) => {
              const cat = a.state_category || "ALERT";
              const style = CATEGORY_STYLE[cat] || { cls: "bg-red-500/15 text-red-300 border-red-400/50", label: "PUBLIC ALERT" };
              return (
                <div key={a.id} className={`rounded p-4 border ${style.cls} bg-zinc-950/40`} data-testid={`state-alert-${a.id?.slice(-12)}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Badge className={`${style.cls} font-bold uppercase text-[10px] tracking-wider`}>{style.label}</Badge>
                    <span className="text-[10px] opacity-75 tabular-nums">{fmtExpiry(a.expires || a.ends)}</span>
                  </div>
                  <div className="text-sm font-bold text-zinc-100 mt-2">{a.event}</div>
                  <div className="text-xs text-zinc-300 mt-1.5">{a.headline}</div>
                  {a.description && (
                    <div className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-6">{a.description.slice(0, 800)}</div>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-2">{a.areaDesc}</div>
                </div>
              );
            })}
          </div>
        </Card>
        <div className="text-[10px] text-zinc-500 text-center">
          Source: NWS CAP gateway (api.weather.gov/alerts/active?area=OK) · refresh 60 s
        </div>
      </div>
    </div>
  );
}
